import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Users, CalendarClock, Package, MessageSquare, Send,
  Plus, Pencil, Trash2, CheckCircle2, XCircle, Reply, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/components/Navbar";

const STATUS = {
  afventer: "bg-amber-100 text-amber-700 border-amber-200",
  godkendt: "bg-blue-100 text-blue-700 border-blue-200",
  "færdig": "bg-emerald-100 text-emerald-700 border-emerald-200",
  afvist: "bg-rose-100 text-rose-700 border-rose-200",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, b, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/bookings"),
        api.get("/admin/customers"),
      ]);
      setStats(s.data); setBookings(b.data); setCustomers(c.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="J-Computer logo" className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-200" />
            <span className="text-lg font-black tracking-tighter font-display text-slate-900">Admin<span className="text-blue-600">.</span></span>
          </div>
          <Button variant="outline" onClick={doLogout} data-testid="admin-logout" className="rounded-full gap-2">
            <LogOut className="h-4 w-4" /> Log ud
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { k: "customers", label: "Kunder", icon: Users },
            { k: "pending_customers", label: "Afventer godkendelse", icon: Users },
            { k: "bookings", label: "Bookinger i alt", icon: CalendarClock },
            { k: "pending_bookings", label: "Nye bookinger", icon: CalendarClock },
            { k: "products", label: "Produkter/ydelser", icon: Package },
          ].map((s) => (
            <div key={s.k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`stat-${s.k}`}>
              <s.icon className="h-4 w-4 text-blue-600" />
              <p className="mt-2 text-2xl font-display font-black text-slate-900">{stats?.[s.k] ?? "—"}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings" className="mt-8">
          <TabsList className="bg-white border border-slate-200 h-auto p-1 rounded-xl flex-wrap">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><CalendarClock className="h-4 w-4" /> Bookinger</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><Users className="h-4 w-4" /> Kunder</TabsTrigger>
            <TabsTrigger value="serviceaftale" data-testid="admin-tab-serviceaftale" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><ShieldCheck className="h-4 w-4" /> Serviceaftale</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><MessageSquare className="h-4 w-4" /> Beskeder</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-5">
            <BookingsPanel bookings={bookings} reload={loadAll} />
          </TabsContent>
          <TabsContent value="customers" className="mt-5">
            <CustomersPanel customers={customers} reload={loadAll} />
          </TabsContent>
          <TabsContent value="serviceaftale" className="mt-5">
            <ServiceSettingsPanel />
          </TabsContent>
          <TabsContent value="messages" className="mt-5">
            <MessagesPanel customers={customers} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ BOOKINGS */
function BookingsPanel({ bookings, reload }) {
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("");

  const openDialog = (b) => { setOpen(b); setReply(b.admin_reply || ""); setStatus(b.status); };

  const save = async () => {
    try {
      await api.patch(`/admin/bookings/${open.booking_id}`, { status, admin_reply: reply });
      toast.success("Booking opdateret");
      setOpen(null); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const quick = async (b, s) => {
    try {
      await api.patch(`/admin/bookings/${b.booking_id}`, { status: s });
      toast.success(`Booking ${s}`); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Kunde</TableHead><TableHead>Kategori</TableHead>
            <TableHead>Status</TableHead><TableHead className="hidden md:table-cell">Dato</TableHead>
            <TableHead className="text-right">Handling</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">Ingen bookinger endnu.</TableCell></TableRow>
          )}
          {bookings.map((b) => (
            <TableRow key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`}>
              <TableCell>
                <p className="font-medium text-slate-900">{b.name}</p>
                <p className="text-xs text-slate-500">{b.email}</p>
              </TableCell>
              <TableCell className="text-slate-700">{b.category}</TableCell>
              <TableCell><Badge className={`border ${STATUS[b.status] || STATUS.afventer}`}>{b.status}</Badge></TableCell>
              <TableCell className="hidden md:table-cell text-slate-500 text-sm">{fmt(b.created_at)}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {b.status === "afventer" && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => quick(b, "godkendt")} data-testid={`booking-approve-${b.booking_id}`} className="h-8 w-8 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => quick(b, "afvist")} data-testid={`booking-reject-${b.booking_id}`} className="h-8 w-8 text-rose-600 hover:bg-rose-50"><XCircle className="h-4 w-4" /></Button>
                    </>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => openDialog(b)} data-testid={`booking-open-${b.booking_id}`} className="h-8 w-8 text-blue-600 hover:bg-blue-50"><Reply className="h-4 w-4" /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Booking · {open?.name}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm space-y-1">
                <p><span className="text-slate-500">Kategori:</span> {open.category}</p>
                {open.device && <p><span className="text-slate-500">Enhed:</span> {open.device}</p>}
                {open.preferred_date && <p><span className="text-slate-500">Ønsket dato:</span> {fmt(open.preferred_date)}</p>}
                <p><span className="text-slate-500">Telefon:</span> {open.phone || "—"}</p>
                <p className="pt-2 text-slate-700 whitespace-pre-line">{open.description}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="booking-status-select" className="bg-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="afventer">Afventer</SelectItem>
                    <SelectItem value="godkendt">Godkendt (giver portaladgang)</SelectItem>
                    <SelectItem value="færdig">Afsluttet</SelectItem>
                    <SelectItem value="afvist">Afvist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Svar til kunden</Label>
                <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="booking-reply-input"
                  placeholder="Skriv et svar (sendes på e-mail)…" className="bg-slate-50 resize-none" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Annuller</Button>
            <Button onClick={save} data-testid="booking-save-btn" className="bg-blue-600 hover:bg-blue-700">Gem & send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ CUSTOMERS */
const emptyCustomer = { name: "", email: "", phone: "", address: "", password: "", approved: true };
function CustomersPanel({ customers, reload }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [detail, setDetail] = useState(null);

  const openNew = () => { setEdit(null); setForm(emptyCustomer); setOpen(true); };
  const openEdit = (c) => { setEdit(c); setForm({ ...c, password: "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      if (edit) await api.patch(`/admin/customers/${edit.user_id}`, form);
      else await api.post("/admin/customers", form);
      toast.success(edit ? "Kunde opdateret" : "Kunde oprettet");
      setOpen(false); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Slet kunden ${c.name}?`)) return;
    try { await api.delete(`/admin/customers/${c.user_id}`); toast.success("Kunde slettet"); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleApprove = async (c) => {
    try { await api.patch(`/admin/customers/${c.user_id}`, { approved: !c.approved }); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <p className="font-display font-bold text-slate-900">Kunder</p>
        <Button onClick={openNew} data-testid="customer-new-btn" className="rounded-full gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Ny kunde</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Navn</TableHead><TableHead className="hidden md:table-cell">Telefon</TableHead>
            <TableHead>Godkendt</TableHead><TableHead className="text-right">Handling</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-10">Ingen kunder endnu.</TableCell></TableRow>}
          {customers.map((c) => (
            <TableRow key={c.user_id} data-testid={`admin-customer-${c.user_id}`}>
              <TableCell><p className="font-medium text-slate-900">{c.name}</p><p className="text-xs text-slate-500">{c.email}</p></TableCell>
              <TableCell className="hidden md:table-cell text-slate-600">{c.phone || "—"}</TableCell>
              <TableCell>
                <Switch checked={!!c.approved} onCheckedChange={() => toggleApprove(c)} data-testid={`customer-approve-${c.user_id}`} />
              </TableCell>
              <TableCell className="text-right">
                <Button variant="outline" size="sm" onClick={() => setDetail(c)} data-testid={`customer-products-${c.user_id}`} className="mr-1 rounded-full gap-1 h-8"><Package className="h-3.5 w-3.5" /> Produkter</Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.user_id}`} className="h-8 w-8 text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c)} data-testid={`customer-delete-${c.user_id}`} className="h-8 w-8 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{edit ? "Rediger kunde" : "Ny kunde"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Navn</Label><Input value={form.name} onChange={set("name")} data-testid="customer-name-input" className="bg-slate-50" /></div>
            <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" value={form.email} onChange={set("email")} disabled={!!edit} data-testid="customer-email-input" className="bg-slate-50" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={set("phone")} data-testid="customer-phone-input" className="bg-slate-50" /></div>
              <div className="space-y-1.5"><Label>{edit ? "Ny adgangskode" : "Adgangskode"}</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="customer-password-input" placeholder={edit ? "(uændret)" : "min. 6 tegn"} className="bg-slate-50" /></div>
            </div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address} onChange={set("address")} data-testid="customer-address-input" className="bg-slate-50" /></div>
            <label className="flex items-center gap-3 pt-1">
              <Switch checked={!!form.approved} onCheckedChange={(v) => setForm({ ...form, approved: v })} data-testid="customer-approved-switch" />
              <span className="text-sm text-slate-700">Godkendt til portaladgang</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuller</Button>
            <Button onClick={save} data-testid="customer-save-btn" className="bg-blue-600 hover:bg-blue-700">Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CustomerDetailDialog customer={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

/* ------------------------------------------------------------------ CUSTOMER PRODUCTS */
const emptyProduct = { name: "", description: "", type: "produkt", price: "", purchase_date: "", expiry_date: "", status: "aktiv", has_agreement: false, agreement_years: "", agreement_billing: "maanedlig", agreement_expiry: "" };
function ProductForm({ userId, plans, discount, editing, onSaved, onCancel }) {
  const [form, setForm] = useState(editing ? {
    ...emptyProduct, ...editing, price: editing.price ?? "",
    agreement_years: editing.agreement_years ?? "",
    agreement_billing: editing.agreement_billing || "maanedlig",
  } : emptyProduct);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name) { toast.error("Angiv et navn"); return; }
    if (form.has_agreement && !form.agreement_years) { toast.error("Vælg aftalelængde"); return; }
    const payload = {
      ...form, user_id: userId,
      price: form.price === "" ? null : Number(form.price),
      agreement_years: form.has_agreement && form.agreement_years ? Number(form.agreement_years) : null,
    };
    try {
      if (editing) await api.patch(`/admin/products/${editing.product_id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Opdateret" : "Tilføjet");
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3" data-testid="product-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Navn</Label><Input value={form.name} onChange={set("name")} data-testid="product-name-input" className="bg-white" placeholder="fx Lenovo ThinkPad" /></div>
        <div className="space-y-1.5">
          <Label>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="product-type-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="produkt">Produkt</SelectItem><SelectItem value="ydelse">Ydelse</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5"><Label>Beskrivelse</Label><Textarea rows={2} value={form.description} onChange={set("description")} data-testid="product-desc-input" className="bg-white resize-none" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>Pris (kr.)</Label><Input type="number" value={form.price} onChange={set("price")} data-testid="product-price-input" className="bg-white" /></div>
        <div className="space-y-1.5"><Label>Købsdato</Label><Input type="date" value={form.purchase_date || ""} onChange={set("purchase_date")} data-testid="product-purchase-input" className="bg-white" /></div>
        <div className="space-y-1.5"><Label>Udløbsdato</Label><Input type="date" value={form.expiry_date || ""} onChange={set("expiry_date")} data-testid="product-expiry-input" className="bg-white" /></div>
      </div>

      <div className="rounded-lg border border-blue-100 bg-white p-3 space-y-3">
        <label className="flex items-center gap-3">
          <Switch checked={!!form.has_agreement} onCheckedChange={(v) => setForm({ ...form, has_agreement: v })} data-testid="product-agreement-toggle" />
          <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-blue-600" /> Tilknyt serviceaftale til dette produkt</span>
        </label>
        {form.has_agreement && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Aftalelængde</Label>
                <Select value={form.agreement_years ? String(form.agreement_years) : ""} onValueChange={(v) => setForm({ ...form, agreement_years: v })}>
                  <SelectTrigger data-testid="product-agreement-plan" className="bg-slate-50"><SelectValue placeholder="Vælg pakke" /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={String(p.years)}>{p.years} år – {p.monthly_price} kr./md</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Aftale udløber</Label><Input type="date" value={form.agreement_expiry || ""} onChange={set("agreement_expiry")} data-testid="product-agreement-expiry" className="bg-slate-50" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Betaling</Label>
              <div className="flex gap-2">
                {[["maanedlig", "Månedlig"], ["aarlig", `Årlig (spar ${discount}%)`]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setForm({ ...form, agreement_billing: v })} data-testid={`product-agreement-billing-${v}`}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${form.agreement_billing === v ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="product-cancel-btn">Annuller</Button>
        <Button onClick={save} data-testid="product-save-btn" className="bg-blue-600 hover:bg-blue-700">Gem</Button>
      </div>
    </div>
  );
}

function CustomerDetailDialog({ customer, onClose }) {
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [discount, setDiscount] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!customer) return;
    try {
      const [p, sp] = await Promise.all([
        api.get(`/admin/products?user_id=${customer.user_id}`),
        api.get("/service-plans"),
      ]);
      setProducts(p.data);
      setPlans(sp.data.plans || []);
      setDiscount(sp.data.annual_discount_pct ?? 25);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  useEffect(() => {
    if (customer) { setShowForm(false); setEditing(null); load(); }
    // eslint-disable-next-line
  }, [customer]);

  const del = async (p) => {
    if (!window.confirm(`Slet "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.product_id}`); toast.success("Slettet"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{customer?.name} · Produkter & køb</DialogTitle></DialogHeader>
        {customer && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{customer.email}{customer.phone ? ` · ${customer.phone}` : ""}</p>
            {!showForm && (
              <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="detail-add-product" className="rounded-full gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Tilføj produkt/ydelse</Button>
            )}
            {showForm && (
              <ProductForm userId={customer.user_id} plans={plans} discount={discount} editing={editing}
                onSaved={() => { setShowForm(false); setEditing(null); load(); }}
                onCancel={() => { setShowForm(false); setEditing(null); }} />
            )}
            <div className="space-y-2" data-testid="detail-products">
              {products.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Ingen produkter endnu.</p>}
              {products.map((p) => {
                const agExp = p.agreement_expiry;
                const expired = agExp && new Date(agExp) < new Date();
                return (
                  <div key={p.product_id} data-testid={`detail-product-${p.product_id}`} className="rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900">{p.name}</p>
                        <Badge variant="outline" className="capitalize border-slate-200 text-slate-500">{p.type}</Badge>
                        {p.price != null && <span className="text-xs text-slate-500">{p.price} kr.</span>}
                      </div>
                      {p.description && <p className="text-sm text-slate-500 mt-0.5">{p.description}</p>}
                      {p.expiry_date && <p className="text-xs text-slate-500 mt-1">Produkt udløber: {fmt(p.expiry_date)}</p>}
                      {p.has_agreement && (
                        <p className={`text-xs mt-1 font-medium flex items-center gap-1 ${expired ? "text-rose-600" : "text-blue-700"}`}>
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Serviceaftale{p.agreement_years ? `: ${p.agreement_years} år` : ""}{agExp ? ` · udløber ${fmt(agExp)}` : ""}{expired ? " (udløbet)" : ""}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`detail-product-edit-${p.product_id}`} className="h-8 w-8 text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(p)} data-testid={`detail-product-delete-${p.product_id}`} className="h-8 w-8 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ SERVICE SETTINGS */
function ServiceSettingsPanel() {
  const [settings, setSettings] = useState({ conditions: "", annual_discount_pct: 25, plans: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/service-plans");
      setSettings({
        conditions: data.conditions || "",
        annual_discount_pct: data.annual_discount_pct ?? 25,
        plans: data.plans || [],
      });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updatePlan = (i, key, value) => {
    setSettings((prev) => ({ ...prev, plans: prev.plans.map((p, idx) => idx === i ? { ...p, [key]: value } : p) }));
  };
  const setPopular = (i) => {
    setSettings((prev) => ({ ...prev, plans: prev.plans.map((p, idx) => ({ ...p, popular: idx === i })) }));
  };
  const addPlan = () => setSettings((prev) => ({
    ...prev,
    plans: [...prev.plans, { id: `p${Date.now()}`, years: 1, monthly_price: 99, features: [], popular: false }],
  }));
  const removePlan = (i) => setSettings((prev) => ({ ...prev, plans: prev.plans.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        conditions: settings.conditions,
        annual_discount_pct: Number(settings.annual_discount_pct) || 0,
        plans: settings.plans.map((p) => ({
          id: String(p.id), years: Number(p.years) || 0, monthly_price: Number(p.monthly_price) || 0,
          popular: !!p.popular,
          features: Array.isArray(p.features) ? p.features
            : String(p.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
        })),
      };
      await api.put("/admin/service-plans", payload);
      toast.success("Serviceaftale gemt");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400">Indlæser…</div>;

  return (
    <div className="space-y-5" data-testid="service-settings-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-display font-bold text-slate-900">Serviceaftale – priser & betingelser</p>
            <p className="text-sm text-slate-500">Ret pakker, priser, årsrabat og betingelser. Ændringer vises straks på forsiden.</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="disc" className="text-sm">Årsrabat %</Label>
            <Input id="disc" type="number" value={settings.annual_discount_pct} data-testid="service-discount-input"
              onChange={(e) => setSettings((prev) => ({ ...prev, annual_discount_pct: e.target.value }))}
              className="h-10 w-24 bg-slate-50" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {settings.plans.map((p, i) => (
          <div key={i} data-testid={`service-plan-editor-${i}`}
            className={`rounded-xl border bg-white p-5 space-y-3 ${p.popular ? "border-blue-600" : "border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-500">Pakke {i + 1}</span>
              <Button size="icon" variant="ghost" onClick={() => removePlan(i)} data-testid={`service-plan-remove-${i}`}
                className="h-8 w-8 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Antal år</Label>
                <Input type="number" value={p.years} data-testid={`service-plan-years-${i}`}
                  onChange={(e) => updatePlan(i, "years", e.target.value)} className="h-10 bg-slate-50" />
              </div>
              <div className="space-y-1.5">
                <Label>Pris kr./md</Label>
                <Input type="number" value={p.monthly_price} data-testid={`service-plan-price-${i}`}
                  onChange={(e) => updatePlan(i, "monthly_price", e.target.value)} className="h-10 bg-slate-50" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Fordele (én pr. linje)</Label>
              <Textarea rows={4} data-testid={`service-plan-features-${i}`}
                value={Array.isArray(p.features) ? p.features.join("\n") : p.features}
                onChange={(e) => updatePlan(i, "features", e.target.value.split("\n"))}
                className="bg-slate-50 resize-none text-sm" />
            </div>
            <label className="flex items-center gap-3">
              <Switch checked={!!p.popular} onCheckedChange={() => setPopular(i)} data-testid={`service-plan-popular-${i}`} />
              <span className="text-sm text-slate-700">Mest populær</span>
            </label>
          </div>
        ))}
      </div>

      <Button onClick={addPlan} variant="outline" data-testid="service-plan-add" className="rounded-full gap-2">
        <Plus className="h-4 w-4" /> Tilføj pakke
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-1.5">
        <Label>Betingelser</Label>
        <Textarea rows={6} value={settings.conditions} data-testid="service-conditions-input"
          onChange={(e) => setSettings((prev) => ({ ...prev, conditions: e.target.value }))}
          className="bg-slate-50 resize-none" />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="service-save-btn"
          className="rounded-full bg-blue-600 hover:bg-blue-700 px-8">
          {saving ? "Gemmer…" : "Gem ændringer"}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ MESSAGES */
function MessagesPanel({ customers }) {
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");

  const loadThread = async (c) => {
    setActive(c);
    try { const { data } = await api.get(`/admin/messages/${c.user_id}`); setThread(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    try {
      const { data } = await api.post("/admin/messages", { user_id: active.user_id, body });
      setThread([...thread, data]); setBody("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <p className="p-4 font-display font-bold text-slate-900 border-b border-slate-200">Kunder</p>
        <div className="max-h-[520px] overflow-y-auto">
          {customers.length === 0 && <p className="p-4 text-sm text-slate-400">Ingen kunder.</p>}
          {customers.map((c) => (
            <button key={c.user_id} onClick={() => loadThread(c)} data-testid={`msg-customer-${c.user_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${active?.user_id === c.user_id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
              <p className="font-medium text-slate-900 text-sm">{c.name}</p>
              <p className="text-xs text-slate-500">{c.email}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white flex flex-col h-[520px]">
        {!active ? (
          <div className="flex-1 grid place-items-center text-slate-400 text-sm">Vælg en kunde for at se beskeder</div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-200"><p className="font-medium text-slate-900">{active.name}</p></div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="admin-message-list">
              {thread.length === 0 ? <p className="text-center text-sm text-slate-400 mt-10">Ingen beskeder endnu.</p> :
                thread.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "admin" ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                      <p>{m.body}</p>
                      <p className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-blue-100" : "text-slate-400"}`}>{m.sender === "admin" ? "Dig" : m.author_name} · {fmt(m.created_at)}</p>
                    </div>
                  </div>
                ))}
            </div>
            <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
              <Input value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-message-input" placeholder="Skriv et svar…" className="bg-slate-50 focus-visible:ring-blue-600" />
              <Button type="submit" data-testid="admin-message-send" className="rounded-lg bg-blue-600 hover:bg-blue-700 shrink-0"><Send className="h-4 w-4" /></Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
